import * as React from 'react';

import { createBrowserRouter, Navigate } from 'react-router-dom';

import { PrivateLayout } from 'layouts';
import { decodeIdentityToken, verifyTokenPermission } from 'lib';
import {
  Attribute,
  Attributes,
  Brand,
  Brands,
  Catalog,
  Catalogs,
  Categories,
  Category,
  DiscountCode,
  DiscountCodes,
  Home,
  Login,
  PackagingModel,
  PackagingModels,
  PageBuilder,
  PageBuilderFooter,
  PaymentGateway,
  PaymentGateways,
  Product,
  ProductComment,
  Products,
  ProductsComments,
  ProductsEdit,
  ProductsReport,
  Purchase,
  Purchases,
  Role,
  Roles,
  RouteError,
  SpecialOffer,
  SpecialOffers,
  Tag,
  Tags,
  User,
  Users,
  WatermarkSetting,
} from 'pages';
import {
  checkAuth,
  getAttributeConfig,
  getBrand,
  getCatalog,
  getCategory,
  getDiscountCode,
  getFooterInfo,
  getMainPageBrands,
  getPackagingModel,
  getPageBanners,
  getPageCategoryProducts,
  getPaymentGateway,
  getProduct,
  getProductComment,
  getPublicNotices,
  getPurchase,
  getRole,
  getShippingConfig,
  getSpecialOffer,
  getTag,
  getUser,
  getWatermark,
  signOut,
} from 'services';
import { getTimeProgress, PATHNAME, throwErrorAsResponse } from 'utils';

import PublicNotices from '../pages/public-notices';
import ShippingConfig from '../pages/shipping-config';
import ShippingConfigs from '../pages/shipping-configs';

const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    HOME: { BASE_URL: HOME_BASE_URL },
    PRODUCTS: {
      BASE_URL: PRODUCTS_BASE_URL,
      CREATE_URL: PRODUCTS_CREATE_URL,
      UPDATE_URL: PRODUCTS_UPDATE_URL,
    },
    CATEGORIES: {
      BASE_URL: CATEGORIES_BASE_URL,
      CREATE_URL: CATEGORIES_CREATE_URL,
      UPDATE_URL: CATEGORIES_UPDATE_URL,
    },
    TAGS: {
      BASE_URL: TAGS_BASE_URL,
      CREATE_URL: TAGS_CREATE_URL,
      UPDATE_URL: TAGS_UPDATE_URL,
    },
    BRANDS: {
      BASE_URL: BRANDS_BASE_URL,
      CREATE_URL: BRANDS_CREATE_URL,
      UPDATE_URL: BRANDS_UPDATE_URL,
    },
    CATALOGS: {
      BASE_URL: CATALOGS_BASE_URL,
      CREATE_URL: CATALOGS_CREATE_URL,
      UPDATE_URL: CATALOGS_UPDATE_URL,
    },
    ATTRIBUTES: {
      BASE_URL: ATTRIBUTES_BASE_URL,
      CREATE_URL: ATTRIBUTES_CREATE_URL,
      UPDATE_URL: ATTRIBUTES_UPDATE_URL,
    },
    PACKAGING_MODELS: {
      BASE_URL: PACKAGING_MODELS_BASE_URL,
      CREATE_URL: PACKAGING_MODELS_CREATE_URL,
      UPDATE_URL: PACKAGING_MODELS_UPDATE_URL,
    },
    PURCHASES: {
      BASE_URL: PURCHASES_BASE_URL,
      UPDATE_URL: PURCHASES_UPDATE_URL,
    },
    PAYMENT_GATEWAYS: {
      BASE_URL: PAYMENT_GATEWAYS_BASE_URL,
      UPDATE_URL: PAYMENT_GATEWAYS_UPDATE_URL,
    },
    USERS: {
      BASE_URL: USERS_BASE_URL,
      CREATE_URL: USERS_CREATE_URL,
      UPDATE_URL: USERS_UPDATE_URL,
    },
    ROLES: {
      BASE_URL: ROLES_BASE_URL,
      CREATE_URL: ROLES_CREATE_URL,
      UPDATE_URL: ROLES_UPDATE_URL,
    },
    PRODUCTS_COMMENTS: {
      BASE_URL: PRODUCTS_COMMENTS_BASE_URL,
      CREATE_URL: PRODUCTS_COMMENTS_CREATE_URL,
      UPDATE_URL: PRODUCTS_COMMENTS_UPDATE_URL,
    },
    DISCOUNT_CODES: {
      BASE_URL: DISCOUNT_CODES_BASE_URL,
      CREATE_URL: DISCOUNT_CODES_CREATE_URL,
      UPDATE_URL: DISCOUNT_CODES_UPDATE_URL,
    },
    SPECIAL_OFFERS: {
      BASE_URL: SPECIAL_OFFERS_BASE_URL,
      CREATE_URL: SPECIAL_OFFERS_CREATE_URL,
      UPDATE_URL: SPECIAL_OFFERS_UPDATE_URL,
    },
    SHIPPING_CONFIGS: {
      BASE_URL: SHIPPING_CONFIGS_BASE_URL,
      CREATE_URL: SHIPPING_CONFIGS_CREATE_URL,
      UPDATE_URL: SHIPPING_CONFIGS_UPDATE_URL,
    },
    PRODUCTS_REPORT: { BASE_URL: PRODUCTS_REPORT_BASE_URL },
    PRODUCTS_EDIT: { BASE_URL: PRODUCTS_EDIT_BASE_URL },
    WATERMARK_SETTING: { BASE_URL: WATERMARK_SETTING_BASE_URL },
    PUBLIC_NOTICES: { BASE_URL: PUBLIC_NOTICES_BASE_URL },
    PAGE_BUILDER: { BASE_URL: PAGE_BUILDER_BASE_URL },
    PAGE_BUILDER_FOOTER: { BASE_URL: PAGE_BUILDER_FOOTER_BASE_URL },
    LOGIN: { BASE_URL: LOGIN_BASE_URL },
  },
  QUERY_PARAM: {
    DEFAULT: { COLON: DEFAULT_COLON },
    ID: { BASE_URL: ID_BASE_URL },
  },
} = PATHNAME;

const router = createBrowserRouter([
  // NOTE: If the user was authenticated, then user can access the private layout routes, otherwise user will be redirected to the login page.
  {
    path: HOME_BASE_URL,
    loader: async () => {
      const { success } = await checkAuth();

      if (!success) {
        throw new Response('Unauthorized', { status: 401 });
      }

      const { decodedAccessToken, decodedRefreshToken } =
        await decodeIdentityToken();

      if (!decodedAccessToken || !decodedRefreshToken) {
        throw new Response('Forbidden', { status: 403 });
      }

      const currentTime = new Date(Date.now() / 1_000);
      const accessExpiryTime = new Date(Number(decodedAccessToken.exp));
      const refreshExpiryTime = new Date(Number(decodedRefreshToken.exp));

      const { isAfterTarget: isTokenExpired } = getTimeProgress(
        refreshExpiryTime,
        currentTime,
        accessExpiryTime,
      );

      if (isTokenExpired) {
        signOut()
          .then(() => {
            return success;
          })
          .catch(() => {
            throw new Response('Service Unavailable', { status: 503 });
          });
      }

      return success;
    },
    element: <PrivateLayout />,
    errorElement: <Navigate to={LOGIN_BASE_URL} />,
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: PRODUCTS_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'product:read',
                  'product:create',
                  'product:modify',
                  'product:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به لیست محصول‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Products />,
            errorElement: <RouteError />,
          },
          {
            path: PRODUCTS_CREATE_URL,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'product:create',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای افزودن محصول را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Product mode='Create' />,
            errorElement: <RouteError />,
          },
          {
            path: `${PRODUCTS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${DEFAULT_COLON}${ID_BASE_URL}`,
            loader: async ({ params: { id } }) => {
              if (!id) return;

              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'product:modify',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای ویرایش محصول‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getProduct({ id });
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Product mode='Update' />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: CATEGORIES_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'category:read',
                  'category:create',
                  'category:modify',
                  'category:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به لیست دسته‌بندی‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Categories />,
            errorElement: <RouteError />,
          },
          {
            path: CATEGORIES_CREATE_URL,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'category:create',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای افزودن دسته‌بندی‌ را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Category mode='Create' />,
            errorElement: <RouteError />,
          },
          {
            path: `${CATEGORIES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${DEFAULT_COLON}${ID_BASE_URL}`,
            loader: async ({ params: { id } }) => {
              if (!id) return;

              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'category:modify',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای ویرایش دسته‌بندی‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getCategory({ id });
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Category mode='Update' />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: TAGS_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'tag:read',
                  'tag:create',
                  'tag:modify',
                  'tag:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به لیست برچسب‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Tags />,
            errorElement: <RouteError />,
          },
          {
            path: TAGS_CREATE_URL,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'tag:create',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای افزودن برچسب را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Tag mode='Create' />,
            errorElement: <RouteError />,
          },
          {
            path: `${TAGS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${DEFAULT_COLON}${ID_BASE_URL}`,
            loader: async ({ params: { id } }) => {
              if (!id) return;

              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'tag:modify',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای ویرایش برچسب‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getTag({ id });
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Tag mode='Update' />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: BRANDS_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'brand:read',
                  'brand:create',
                  'brand:modify',
                  'brand:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به لیست برندها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Brands />,
            errorElement: <RouteError />,
          },
          {
            path: BRANDS_CREATE_URL,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'brand:create',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای افزودن برند را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Brand mode='Create' />,
            errorElement: <RouteError />,
          },
          {
            path: `${BRANDS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${DEFAULT_COLON}${ID_BASE_URL}`,
            loader: async ({ params: { id } }) => {
              if (!id) return;

              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'brand:modify',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای ویرایش برندها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getBrand({ id });
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Brand mode='Update' />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: CATALOGS_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'catalog:read',
                  'catalog:create',
                  'catalog:modify',
                  'catalog:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به لیست کاتالوگ‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Catalogs />,
            errorElement: <RouteError />,
          },
          {
            path: CATALOGS_CREATE_URL,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'catalog:create',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای افزودن کاتالوگ را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Catalog mode='Create' />,
            errorElement: <RouteError />,
          },
          {
            path: `${CATALOGS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${DEFAULT_COLON}${ID_BASE_URL}`,
            loader: async ({ params: { id } }) => {
              if (!id) return;

              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'catalog:modify',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای ویرایش کاتالوگ‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getCatalog({ id });
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Catalog mode='Update' />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: ATTRIBUTES_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'attribute-config:read',
                  'attribute-config:create',
                  'attribute-config:modify',
                  'attribute-config:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به لیست فیلدها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Attributes />,
            errorElement: <RouteError />,
          },
          {
            path: ATTRIBUTES_CREATE_URL,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'attribute-config:create',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای افزودن فیلد را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Attribute mode='Create' />,
            errorElement: <RouteError />,
          },
          {
            path: `${ATTRIBUTES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${DEFAULT_COLON}${ID_BASE_URL}`,
            loader: async ({ params: { id } }) => {
              if (!id) return;

              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'attribute-config:modify',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای ویرایش فیلدها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getAttributeConfig({ id });
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Attribute mode='Update' />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: PACKAGING_MODELS_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'packaging-model:read',
                  'packaging-model:create',
                  'packaging-model:modify',
                  'packaging-model:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به لیست بسته‌بندی‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <PackagingModels />,
            errorElement: <RouteError />,
          },
          {
            path: PACKAGING_MODELS_CREATE_URL,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'packaging-model:create',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای افزودن بسته‌بندی را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <PackagingModel mode='Create' />,
            errorElement: <RouteError />,
          },
          {
            path: `${PACKAGING_MODELS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${DEFAULT_COLON}${ID_BASE_URL}`,
            loader: async ({ params: { id } }) => {
              if (!id) return;

              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'packaging-model:modify',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای ویرایش بسته‌بندی‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getPackagingModel({ id });
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <PackagingModel mode='Update' />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: PURCHASES_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'purchase:read',
                  'purchase:create',
                  'purchase:modify',
                  'purchase:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به لیست سفارش‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Purchases />,
            errorElement: <RouteError />,
          },
          {
            path: `${PURCHASES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${DEFAULT_COLON}${ID_BASE_URL}`,
            loader: async ({ params: { id } }) => {
              if (!id) return;

              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'purchase:modify',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای ویرایش سفارش‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getPurchase({ id });
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Purchase mode='Update' />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: PAYMENT_GATEWAYS_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'payment-gateway:read',
                  'payment-gateway:create',
                  'payment-gateway:modify',
                  'payment-gateway:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به لیست درگاه‌های پرداخت را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <PaymentGateways />,
            errorElement: <RouteError />,
          },
          {
            path: `${PAYMENT_GATEWAYS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${DEFAULT_COLON}${ID_BASE_URL}`,
            loader: async ({ params: { id } }) => {
              if (!id) return;

              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'payment-gateway:modify',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای ویرایش درگاه‌های پرداخت را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getPaymentGateway({ id });
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <PaymentGateway mode='Update' />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: USERS_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'user:read',
                  'user:create',
                  'user:modify',
                  'user:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به لیست کاربرها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Users />,
            errorElement: <RouteError />,
          },
          {
            path: USERS_CREATE_URL,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'user:create',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای افزودن کاربر را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <User mode='Create' />,
            errorElement: <RouteError />,
          },
          {
            path: `${USERS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${DEFAULT_COLON}${ID_BASE_URL}`,
            loader: async ({ params: { id } }) => {
              if (!id) return;

              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'user:modify',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای ویرایش کاربرها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getUser({ id });
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <User mode='Update' />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: ROLES_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما مجوز لازم برای لیست دسترسی‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Roles />,
            errorElement: <RouteError />,
          },
          {
            path: ROLES_CREATE_URL,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما مجوز لازم برای افزودن دسترسی را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Role mode='Create' />,
            errorElement: <RouteError />,
          },
          {
            path: `${ROLES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${DEFAULT_COLON}${ID_BASE_URL}`,
            loader: async ({ params: { id } }) => {
              if (!id) return;

              try {
                const hasTokenPermission = await verifyTokenPermission([]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما مجوز لازم برای ویرایش دسترسی‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getRole({ id });
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <Role mode='Update' />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: PRODUCTS_COMMENTS_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'product-comment:read',
                  'product-comment:create',
                  'product-comment:modify',
                  'product-comment:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به لیست دیدگاه کاربرها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <ProductsComments />,
            errorElement: <RouteError />,
          },
          {
            path: PRODUCTS_COMMENTS_CREATE_URL,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'product-comment:create',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای افزودن دیدگاه کاربر را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <ProductComment mode='Create' />,
            errorElement: <RouteError />,
          },
          {
            path: `${PRODUCTS_COMMENTS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${DEFAULT_COLON}${ID_BASE_URL}`,
            loader: async ({ params: { id } }) => {
              if (!id) return;

              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'product-comment:modify',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای ویرایش دیدگاه کاربرها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getProductComment({ id });
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <ProductComment mode='Update' />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: DISCOUNT_CODES_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'discount:read',
                  'discount:create',
                  'discount:modify',
                  'discount:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به لیست کد تخفیف‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <DiscountCodes />,
            errorElement: <RouteError />,
          },
          {
            path: DISCOUNT_CODES_CREATE_URL,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'discount:create',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای افزودن کد تخفیف را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <DiscountCode mode='Create' />,
            errorElement: <RouteError />,
          },
          {
            path: `${DISCOUNT_CODES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${DEFAULT_COLON}${ID_BASE_URL}`,
            loader: async ({ params: { id } }) => {
              if (!id) return;

              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'discount:modify',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای ویرایش کد تخفیف‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getDiscountCode({ id });
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <DiscountCode mode='Update' />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: SHIPPING_CONFIGS_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'shipping-config:read',
                  'shipping-config:create',
                  'shipping-config:modify',
                  'shipping-config:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به لیست تنظیمات ارسال را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <ShippingConfigs />,
            errorElement: <RouteError />,
          },
          {
            path: SHIPPING_CONFIGS_CREATE_URL,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'shipping-config:create',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای افزودن تنظیمات ارسال را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <ShippingConfig mode='Create' />,
            errorElement: <RouteError />,
          },
          {
            path: `${SHIPPING_CONFIGS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${DEFAULT_COLON}${ID_BASE_URL}`,
            loader: async ({ params: { id } }) => {
              if (!id) return;

              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'shipping-config:modify',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای ویرایش تنظیمات ارسال را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getShippingConfig({ id });
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <ShippingConfig mode='Update' />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: SPECIAL_OFFERS_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'special-offer:create',
                  'special-offer:create',
                  'special-offer:modify',
                  'special-offer:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به لیست تخفیف‌های ویژه را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <SpecialOffers />,
            errorElement: <RouteError />,
          },
          {
            path: SPECIAL_OFFERS_CREATE_URL,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'special-offer:create',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای افزودن تخفیف ویژه را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <SpecialOffer mode='Create' />,
            errorElement: <RouteError />,
          },
          {
            path: `${SPECIAL_OFFERS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${DEFAULT_COLON}${ID_BASE_URL}`,
            loader: async ({ params: { id } }) => {
              if (!id) return;

              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'special-offer:modify',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم برای ویرایش تخفیف‌های ویژه را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getSpecialOffer({ id });
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <SpecialOffer mode='Update' />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: PRODUCTS_REPORT_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'product:read',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به گزارش‌ محصول‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <ProductsReport />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: PRODUCTS_EDIT_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'product:modify',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به ویرایش محصول‌ها را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return true;
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <ProductsEdit />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: WATERMARK_SETTING_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'config:read',
                  'config:create',
                  'config:modify',
                  'config:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به تنظیمات نهان‌نگاری را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getWatermark();
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <WatermarkSetting />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: PUBLIC_NOTICES_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'config:read',
                  'config:create',
                  'config:modify',
                  'config:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به تنظیمات اطلاع‌رسانی را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                return await getPublicNotices();
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <PublicNotices />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: PAGE_BUILDER_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'config:read',
                  'config:create',
                  'config:modify',
                  'config:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response(
                    'شما دسترسی لازم به صفحه اصلی را ندارید.',
                    {
                      status: 403,
                    },
                  );
                }

                const [
                  { brands },
                  { pageSettings: categoryProductsSettings },
                  { pageSettings: bannersSettings },
                ] = await Promise.all([
                  getMainPageBrands(),
                  getPageCategoryProducts(),
                  getPageBanners(),
                ]);

                return {
                  brands,
                  categoryProductsSettings,
                  bannersSettings,
                };
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <PageBuilder />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: PAGE_BUILDER_FOOTER_BASE_URL,
        children: [
          {
            index: true,
            loader: async () => {
              try {
                const hasTokenPermission = await verifyTokenPermission([
                  'config:read',
                  'config:create',
                  'config:modify',
                  'config:delete',
                ]);

                if (!hasTokenPermission) {
                  throw new Response('شما دسترسی لازم به فوتر را ندارید.', {
                    status: 403,
                  });
                }

                return await getFooterInfo();
              } catch (error) {
                throwErrorAsResponse(error);
              }
            },
            element: <PageBuilderFooter />,
            errorElement: <RouteError />,
          },
        ],
      },
      {
        path: '*',
        element: <RouteError />,
      },
    ],
  },
  // NOTE: If the user is not authenticated, the login page will be displayed to user, otherwise user will be redirected to the main page
  {
    path: LOGIN_BASE_URL,
    element: <Login />,
  },
]);

export { router };
